import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Shield, Eye, EyeOff, CheckCircle2, AlertTriangle } from "lucide-react";
import { getApiBase } from "../config"; // adjust import to your actual helper file
import { useAuth } from "../context/AuthContext"; // adjust path if needed

type AcceptResponse = {
  access_token?: string;
  token?: string; // some backends return token key
  role?: string;
  org_id?: string | null;
  message?: string;
};

export default function InviteAcceptPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { login } = useAuth();

  const tokenFromUrl = useMemo(() => params.get("token") || "", [params]);

  const [token, setToken] = useState(tokenFromUrl);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    setToken(tokenFromUrl);
  }, [tokenFromUrl]);

  const canSubmit =
    token.trim().length >= 20 &&
    fullName.trim().length >= 2 &&
    password.length >= 8 &&
    confirm.length >= 8 &&
    password === confirm &&
    !loading;

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    setSuccessMsg(null);

    if (!token || token.trim().length < 20) {
      setErrorMsg("Invalid or missing invite token.");
      return;
    }
    if (fullName.trim().length < 2) {
      setErrorMsg("Please enter your full name.");
      return;
    }
    if (password.length < 8) {
      setErrorMsg("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      setErrorMsg("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      const apiBase = getApiBase(); // should be "/api"
      const res = await fetch(`${apiBase}/org/users/invite/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: token.trim(),
          password,
          full_name: fullName.trim(),
        }),
      });

      const text = await res.text();
      let data: any = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        const detail =
          data?.detail ||
          data?.message ||
          (text && text.length < 300 ? text : null) ||
          "Invite acceptance failed.";
        setErrorMsg(String(detail));
        return;
      }

      const payload = (data || {}) as AcceptResponse;
      const accessToken = payload.access_token || payload.token;

      // If backend returns a login token, auto-login
      if (accessToken) {
        const role = (payload.role || "USER") as any;
        const orgId = payload.org_id ?? null;
        login(accessToken, role, orgId);

        setSuccessMsg("Account activated. Redirecting…");
        setTimeout(() => navigate("/dashboard", { replace: true }), 600);
        return;
      }

      // Otherwise, send them to login
      setSuccessMsg(payload.message || "Account activated. Please log in.");
      setTimeout(() => navigate("/login", { replace: true }), 900);
    } catch (err: any) {
      setErrorMsg(err?.message || "Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100">
      <div className="w-full max-w-md">
        {/* Brand header */}
        <div className="flex items-center justify-center gap-2 mb-6">
          <div className="w-10 h-10 rounded-xl bg-blue-600/10 dark:bg-blue-500/15 flex items-center justify-center">
            <Shield className="text-blue-600 dark:text-blue-400" />
          </div>
          <div>
            <div className="text-lg font-extrabold tracking-tight">iRedact</div>
            <div className="text-xs text-slate-500 dark:text-slate-500">
              Accept invitation
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
          <div className="p-6 border-b border-slate-200 dark:border-slate-800">
            <h1 className="text-base font-semibold">Activate your account</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Set your password to join your organization workspace.
            </p>
          </div>

          <form onSubmit={onSubmit} className="p-6 space-y-4">
            {/* Token */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Invite token
              </label>
              <input
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Invite token"
                className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-600/30"
              />
              <p className="text-[11px] text-slate-500 dark:text-slate-500 mt-1">
                Usually auto-filled from the invite link.
              </p>
            </div>

            {/* Full name */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Full name
              </label>
              <input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Your full name"
                className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-600/30"
              />
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-2 pr-10 text-sm outline-none focus:ring-2 focus:ring-blue-600/30"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                  aria-label="Toggle password visibility"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            {/* Confirm */}
            <div>
              <label className="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">
                Confirm password
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  placeholder="Re-enter password"
                  className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 px-3 py-2 pr-10 text-sm outline-none focus:ring-2 focus:ring-blue-600/30"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-md text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"
                  aria-label="Toggle confirm password visibility"
                >
                  {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>

              {password && confirm && (
                <div className="mt-2 text-xs">
                  {password === confirm ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 size={14} /> Passwords match
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400">
                      <AlertTriangle size={14} /> Passwords do not match
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Alerts */}
            {errorMsg && (
              <div className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">
                {errorMsg}
              </div>
            )}
            {successMsg && (
              <div className="rounded-lg border border-emerald-200 dark:border-emerald-900/50 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300">
                {successMsg}
              </div>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={!canSubmit}
              className={`w-full rounded-lg px-4 py-2 text-sm font-semibold transition
                ${
                  canSubmit
                    ? "bg-blue-600 hover:bg-blue-700 text-white shadow-md shadow-blue-600/20"
                    : "bg-slate-200 dark:bg-slate-800 text-slate-500 cursor-not-allowed"
                }`}
            >
              {loading ? "Activating…" : "Activate account"}
            </button>

            <div className="text-xs text-slate-500 dark:text-slate-500 text-center">
              Already activated?{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="text-blue-600 dark:text-blue-400 font-semibold hover:underline"
              >
                Go to login
              </button>
            </div>
          </form>
        </div>

        <div className="mt-4 text-center text-[11px] text-slate-500 dark:text-slate-600">
          If you didn’t request this invite, you can safely ignore it.
        </div>
      </div>
    </div>
  );
}
